import { useEffect, useRef, useState } from "react";
import { useCreate, useGetIdentity, useNotify, useRefresh } from "ra-core";
import type { Identifier } from "ra-core";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useSendClientSms } from "@/modules/messages/useClientSms";

/**
 * Small reply box under a timeline text or team-chat entry. Texts go to the
 * client by SMS in the same conversation; team chat posts an internal
 * message. Enter sends, Shift+Enter adds a line, Esc closes.
 */
export const TimelineMessageReply = ({
  conversationId,
  channel,
  to,
  onDone,
}: {
  conversationId: Identifier;
  channel: "sms" | "team";
  /** Shown as "To …" (the client's phone for texts). */
  to?: string | null;
  onDone: () => void;
}) => {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const sendClientSms = useSendClientSms();
  const [create] = useCreate();
  const { data: identity } = useGetIdentity();
  const notify = useNotify();
  const refresh = useRefresh();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const send = async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      if (channel === "sms") {
        await sendClientSms({ conversationId, body: text });
      } else {
        await create(
          "conversation_messages",
          {
            data: {
              conversation_id: conversationId,
              author_member_id: identity?.id,
              body: text,
              channel: "internal",
              direction: "outbound",
            },
          },
          { returnPromise: true },
        );
      }
      notify(channel === "sms" ? "Text sent" : "Message posted", {
        type: "info",
      });
      refresh();
      onDone();
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Could not send the message",
        { type: "error" },
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-2 rounded-lg border bg-background p-2 shadow-sm">
      {to ? (
        <p className="px-1 pb-1 text-2xs text-muted-foreground">To {to}</p>
      ) : null}
      <Textarea
        ref={inputRef}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            void send();
          }
          if (event.key === "Escape") onDone();
        }}
        placeholder={
          channel === "sms" ? "Reply by text…" : "Reply to the team…"
        }
        rows={2}
        className="min-h-0 resize-none border-0 px-1 shadow-none focus-visible:ring-0"
      />
      <div className="flex items-center justify-end gap-1 pt-1">
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!body.trim() || sending}
          onClick={() => void send()}
        >
          {sending ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Send className="size-3.5" />
          )}
          Send
        </Button>
      </div>
    </div>
  );
};
